import { fetcher } from '#lib/backendFetcher';

/**
 * Fetches all email accounts of the authenticated user.
 * @returns {Promise<{accounts: Array}>}
 */
export const getAllEmailAccounts = async () => {
	return fetcher('/api/v1/email/accounts', { method: 'GET' });
};

/**
 * Fetches a single email account by ID.
 * @param {string} accountId - Account ID
 * @returns {Promise<{account: Object}>}
 */
export const getEmailAccountById = async (accountId) => {
	return fetcher(`/api/v1/email/accounts/${accountId}`, { method: 'GET' });
};

/**
 * Creates a new SMTP email account.
 * @param {Object} data - Account data (host, port, username, password, TLS, sender address)
 * @returns {Promise<{account: Object}>}
 */
export const createSmtpAccount = async (data) => {
	return fetcher('/api/v1/email/accounts/smtp', {
		method: 'POST',
		body: data,
	});
};

/**
 * Updates an existing SMTP email account.
 * @param {string} accountId - Account ID
 * @param {Object} data - Account data to update
 * @returns {Promise<{account: Object}>}
 */
export const updateSmtpAccount = async (accountId, data) => {
	return fetcher(`/api/v1/email/accounts/${accountId}/smtp`, {
		method: 'PUT',
		body: data,
	});
};

/**
 * Deletes an email account by ID.
 * @param {string} accountId - Account ID
 * @returns {Promise<void>}
 */
export const deleteEmailAccount = async (accountId) => {
	return fetcher(`/api/v1/email/accounts/${accountId}`, {
		method: 'DELETE',
	});
};

/**
 * Tests an SMTP connection without creating an account.
 * @param {Object} data - SMTP connection data (host, port, username, password, TLS)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const testSmtpConnection = async (data) => {
	return fetcher('/api/v1/email/accounts/test-smtp', {
		method: 'POST',
		body: data,
	});
};

/**
 * Starts the OAuth authorization flow for an email provider.
 * @param {string} provider - Provider name ('outlook', 'office365')
 * @returns {Promise<{authUrl: string, state: string}>}
 */
export const startOAuthFlow = async (provider) => {
	return fetcher(`/api/v1/email/accounts/oauth/authorize/${provider}`, {
		method: 'GET',
	});
};

/**
 * Tests the connection of an existing email account (SMTP or OAuth).
 * @param {string} accountId - Account ID
 * @returns {Promise<{success: boolean, message: string, status: string}>}
 */
export const testEmailAccountConnection = async (accountId) => {
	return fetcher(`/api/v1/email/accounts/${accountId}/test-connection`, {
		method: 'POST',
	});
};

/**
 * Starts the OAuth reauthorization flow for an existing account.
 * @param {string} accountId - Account ID
 * @returns {Promise<{authUrl: string, state: string}>}
 */
export const reauthorizeOAuthAccount = async (accountId) => {
	return fetcher(`/api/v1/email/accounts/${accountId}/reauthorize-oauth`, {
		method: 'POST',
	});
};
